import { Card, Stack, Group, Text, Badge, List, Divider, Alert, Paper, Grid, ThemeIcon, Title } from '@mantine/core';
import {
  IconCircleCheck,
  IconAlertCircle,
  IconClock,
  IconUsers,
  IconSun,
  IconMoon,
  IconCalendar,
  IconBolt,
  IconArrowRight,
  IconInfoCircle
} from '@tabler/icons-react';
import type { TariffType } from '../../../../types/calculator';

/**
 * Tariff Metadata
 * Comprehensive information about each tariff type
 */
interface TariffMetadata {
  name: string;
  type: string;
  description: string;
  bestFor: string[];
  timeZones?: {
    description: string;
    zones: string[];
  };
  advantages: string[];
  disadvantages: string[];
  icon: typeof IconBolt;
  color: string;
}

/**
 * Tariff Metadata Database (Household tariffs only)
 */
const TARIFF_METADATA: Partial<Record<TariffType, TariffMetadata>> = {
  G11: {
    name: 'G11',
    type: 'Jednostrefowa',
    description: 'Jedna stała stawka przez całą dobę, każdego dnia roku',
    bestFor: [
      'Osoby z równomiernym zużyciem energii w ciągu dnia',
      'Gospodarstwa domowe bez możliwości przesunięcia zużycia',
      'Osoby ceniące prostotę rozliczeń'
    ],
    advantages: [
      'Najprostsza taryfa w rozliczeniu',
      'Brak konieczności planowania zużycia energii',
      'Przewidywalne koszty niezależnie od pory dnia',
      'Nie wymaga zmiany nawyków'
    ],
    disadvantages: [
      'Brak możliwości optymalizacji kosztów',
      'Wyższa cena energii w nocy niż w taryfach strefowych',
      'Brak korzyści dla osób mogących przesunąć zużycie',
      'Średnio najdroższa opcja dla dużego zużycia'
    ],
    icon: IconBolt,
    color: 'blue'
  },
  G12: {
    name: 'G12',
    type: 'Dwustrefowa',
    description: 'Dwie strefy cenowe: tańsza w nocy (22:00-6:00) i wczesnym popołudniem (13:00-15:00)',
    bestFor: [
      'Osoby mogące przesunąć zużycie na godziny nocne',
      'Gospodarstwa z pralnią, zmywarką, podgrzewaczem wody',
      'Właściciele pojazdów elektrycznych ładowanych w nocy',
      'Osoby pracujące w dzień, aktywne wieczorami'
    ],
    timeZones: {
      description: 'Strefy czasowe',
      zones: [
        'Strefa tańsza (noc): 22:00-6:00 + 13:00-15:00',
        'Strefa droższa (dzień): 6:00-13:00 + 15:00-22:00'
      ]
    },
    advantages: [
      'Do 50% tańsza energia w strefie nocnej',
      'Możliwość znacznych oszczędności przy odpowiednim planowaniu',
      'Idealna do ładowania pojazdów elektrycznych',
      'Dodatkowa strefa tańsza w południe (13:00-15:00)'
    ],
    disadvantages: [
      'Wymaga planowania i przesunięcia zużycia',
      'Droższa energia w godzinach dziennych',
      'Może być niekorzystna dla osób pracujących zdalnie',
      'Wymaga zmiany nawyków użytkowania energii'
    ],
    icon: IconMoon,
    color: 'indigo'
  },
  G12as: {
    name: 'G12as',
    type: 'Dwustrefowa antysmogowa',
    description: 'Wariant G12 z dodatkowymi korzyściami w sezonie grzewczym dla pomp ciepła',
    bestFor: [
      'Właściciele pomp ciepła',
      'Gospodarstwa z ogrzewaniem elektrycznym',
      'Osoby świadome ekologicznie',
      'Regiony o wysokim zanieczyszczeniu powietrza'
    ],
    timeZones: {
      description: 'Strefy czasowe (jak G12)',
      zones: [
        'Strefa tańsza: 22:00-6:00 + 13:00-15:00',
        'Strefa droższa: 6:00-13:00 + 15:00-22:00',
        'Dodatkowe korzyści w sezonie grzewczym (X-IV)'
      ]
    },
    advantages: [
      'Wszystkie zalety G12 plus korzyści ekologiczne',
      'Korzystniejsze stawki dla pomp ciepła',
      'Wsparcie dla ekologicznych źródeł ogrzewania',
      'Może być tańsza od G12 w sezonie grzewczym'
    ],
    disadvantages: [
      'Dostępna tylko w wybranych regionach',
      'Wymaga posiadania pompy ciepła lub ogrzewania elektrycznego',
      'Bardziej złożone rozliczenia niż standardowa G12',
      'Korzyści głównie w okresie zimowym'
    ],
    icon: IconSun,
    color: 'green'
  },
  G12w: {
    name: 'G12w',
    type: 'Dwustrefowa weekendowa',
    description: 'Jak G12, ale z dodatkowymi tańszymi godzinami w całe weekendy i święta',
    bestFor: [
      'Osoby pracujące w tygodniu, aktywne w weekendy',
      'Rodziny spędzające weekendy w domu',
      'Gospodarstwa z dużym zużyciem w soboty i niedziele',
      'Osoby z elastycznym harmonogramem w weekendy'
    ],
    timeZones: {
      description: 'Strefy czasowe',
      zones: [
        'PN-PT: Jak G12 (noc 22:00-6:00 + 13:00-15:00 tańsze)',
        'Sobota: Całą dobę strefa tańsza',
        'Niedziela i święta: Całą dobę strefa tańsza'
      ]
    },
    advantages: [
      'Tania energia przez całe weekendy i święta',
      'Elastyczność w soboty i niedziele',
      'Możliwość przesunięcia energochłonnych zadań na weekend',
      'Idealna dla rodzin aktywnych w weekendy'
    ],
    disadvantages: [
      'Wyższa opłata dystrybucyjna niż G12',
      'Dostępna tylko u operatora Tauron',
      'Wymaga planowania w tygodniu (jak G12)',
      'Może być droższa niż G12 przy małym zużyciu weekendowym'
    ],
    icon: IconCalendar,
    color: 'violet'
  },
  G13: {
    name: 'G13',
    type: 'Trzystrefowa',
    description: 'Trzy strefy cenowe: szczyt, półszczyt, poza szczytem (zmienne latem i zimą)',
    bestFor: [
      'Osoby z bardzo dużym zużyciem energii',
      'Gospodarstwa mogące precyzyjnie planować zużycie w 3 strefach',
      'Zaawansowani użytkownicy chcący maksymalizować oszczędności',
      'Właściciele magazynów energii'
    ],
    timeZones: {
      description: 'Strefy czasowe (zmienne sezonowo)',
      zones: [
        'Szczyt (najdroższa): 7:00-13:00, 19:00-22:00 (zima)',
        'Półszczyt (średnia): 6:00-7:00, 13:00-16:00, 22:00-23:00',
        'Poza szczytem (najtańsza): 23:00-6:00, 16:00-19:00',
        'Strefy różnią się latem (IV-IX) i zimą (X-III)'
      ]
    },
    advantages: [
      'Najtańsza strefa "poza szczytem" ze wszystkich taryf',
      'Największy potencjał oszczędności przy optymalizacji',
      'Trzy poziomy cen dają większą elastyczność',
      'Możliwość wykorzystania tanich godzin popołudniowych'
    ],
    disadvantages: [
      'Najbardziej skomplikowana w planowaniu',
      'Dostępna tylko u operatora Tauron',
      'Wymaga zaawansowanego zarządzania zużyciem',
      'Strefy różnią się sezonowo - trudniej zapamiętać',
      'Najdroższa strefa szczytowa może być bardzo kosztowna'
    ],
    icon: IconClock,
    color: 'orange'
  },
  G13s: {
    name: 'G13s',
    type: 'Trzystrefowa inteligentna',
    description: 'Nowoczesna wersja G13 z wykorzystaniem OZE i inteligentnych liczników',
    bestFor: [
      'Właściciele paneli fotowoltaicznych',
      'Posiadacze inteligentnych liczników',
      'Osoby korzystające z OZE (odnawialnych źródeł energii)',
      'Zaawansowani użytkownicy z systemami zarządzania energią'
    ],
    timeZones: {
      description: 'Strefy czasowe (jak G13 + zarządzanie OZE)',
      zones: [
        'Szczyt, półszczyt, poza szczytem (jak G13)',
        'Automatyczna optymalizacja z fotowoltaiką',
        'Integracja z inteligentnym licznikiem'
      ]
    },
    advantages: [
      'Wszystkie zalety G13 plus korzyści z OZE',
      'Integracja z panelami fotowoltaicznymi',
      'Wykorzystanie inteligentnego zarządzania energią',
      'Najnowocześniejsza taryfa na rynku',
      'Możliwość automatyzacji zarządzania energią'
    ],
    disadvantages: [
      'Wymaga inteligentnego licznika',
      'Najnowsza - mniej danych o rzeczywistych oszczędnościach',
      'Skomplikowana jak G13',
      'Wymaga inwestycji w systemy OZE dla pełnych korzyści',
      'Dostępna tylko dla wybranych użytkowników'
    ],
    icon: IconSun,
    color: 'teal'
  },
  G14: {
    name: 'G14',
    type: 'Dynamiczna RCE',
    description: 'Ceny energii zmieniające się co godzinę na podstawie Rynkowej Ceny Energii (RCE) z PSE',
    bestFor: [
      'Użytkownicy z systemami automatyzacji smart home',
      'Właściciele magazynów energii',
      'Osoby śledzące ceny RCE i optymalizujące zużycie',
      'Prosumenci z elastycznym zużyciem'
    ],
    timeZones: {
      description: 'Dynamiczne strefy czasowe',
      zones: [
        'Ceny godzinowe 0-23 (zmienne codziennie)',
        'Publikacja cen z jednodniowym wyprzedzeniem',
        'Możliwość przewidywania i optymalizacji'
      ]
    },
    advantages: [
      'Najniższe ceny w godzinach nadprodukcji (często w nocy i weekendy)',
      'Możliwość wykorzystania automatyzacji do oszczędności',
      'Transparentność - ceny odzwierciedlają rzeczywisty rynek',
      'Potencjał do znacznych oszczędności przy właściwym zarządzaniu',
      'Wspiera równoważenie sieci energetycznej'
    ],
    disadvantages: [
      'Wymaga inteligentnego licznika',
      'Ceny mogą być wysokie w szczytach zapotrzebowania',
      'Brak przewidywalności miesięcznych kosztów',
      'Wymaga aktywnego zarządzania lub automatyzacji',
      'Ryzyko wysokich rachunków bez optymalizacji',
      'Nowość na rynku - ograniczone dane o oszczędnościach'
    ],
    icon: IconBolt,
    color: 'violet'
  },
  G12n: {
    name: 'G12n',
    type: 'Nocna (PGE)',
    description: 'Specyficzna taryfa PGE z tanią nocą 01:00-05:00 w dni robocze i całodobowa w weekendy',
    bestFor: [
      'Osoby z elastycznym zużyciem weekendowym',
      'Ładowanie pojazdów elektrycznych w nocy',
      'Gospodarstwa aktywne głównie w weekendy',
      'Osoby mogące przesunąć zużycie na weekend'
    ],
    timeZones: {
      description: 'Strefy czasowe (PGE)',
      zones: [
        'Dni robocze - noc (tańsza): 01:00-05:00 (4 godziny)',
        'Dni robocze - dzień (droższa): 05:00-01:00 (20 godzin)',
        'Weekendy i święta: Całą dobę strefa tańsza'
      ]
    },
    advantages: [
      'Całodobowa tania energia w weekendy',
      'Możliwość przesunięcia dużego zużycia na sobotę/niedzielę',
      'Idealna dla rodzin weekendowych',
      'Korzystna stawka nocna w dni robocze'
    ],
    disadvantages: [
      'Dostępna tylko u operatora PGE',
      'Bardzo krótka strefa nocna w dni robocze (4h)',
      'Droga energia przez większość dnia roboczego',
      'Wymaga planowania zużycia weekendowego'
    ],
    icon: IconMoon,
    color: 'indigo'
  },
  G12r: {
    name: 'G12r',
    type: 'Rozszerzona weekendowa (Energa)',
    description: 'Specyficzna taryfa Energa z dłuższą nocą 21:00-07:00 i tanimi weekendami',
    bestFor: [
      'Osoby z wieczornym zużyciem energii',
      'Ładowanie pojazdów elektrycznych po 21:00',
      'Gospodarstwa aktywne w godzinach wieczornych',
      'Osoby pracujące w dzień, aktywne wieczorami'
    ],
    timeZones: {
      description: 'Strefy czasowe (Energa)',
      zones: [
        'Dni robocze - peak (droższa): 07:00-21:00 (14 godzin)',
        'Dni robocze - off-peak (tańsza): 21:00-07:00 (10 godzin)',
        'Weekendy i święta: Całą dobę strefa off-peak'
      ]
    },
    advantages: [
      'Dłuższa tania strefa nocna niż standardowa G12',
      'Całodobowe weekendy off-peak',
      'Idealna dla wieczornych użytkowników',
      'Elastyczna dla osób pracujących w dzień'
    ],
    disadvantages: [
      'Dostępna tylko u operatora Energa',
      'Droższa energia w godzinach dziennych 07:00-21:00',
      'Wymaga przesunięcia zużycia na wieczór/noc',
      'Może być niekorzystna przy dużym zużyciu dziennym'
    ],
    icon: IconCalendar,
    color: 'orange'
  }
};

/**
 * Props for TariffComparisonDetail component
 */
interface TariffComparisonDetailProps {
  currentTariff: TariffType;
  recommendedTariff: TariffType;
  savingsAmount: number;
  savingsPercent: number;
}

/**
 * TariffComparisonDetail Component
 *
 * Displays comprehensive side-by-side comparison of current vs recommended tariff
 * including characteristics, pros/cons, and what will change.
 */
export function TariffComparisonDetail({
  currentTariff,
  recommendedTariff,
  savingsAmount,
  savingsPercent
}: TariffComparisonDetailProps) {
  const currentMeta = TARIFF_METADATA[currentTariff];
  const recommendedMeta = TARIFF_METADATA[recommendedTariff];

  // Guard against undefined metadata (for business tariffs not yet in metadata)
  if (!currentMeta || !recommendedMeta) {
    return null;
  }

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  /**
   * Render single tariff card
   */
  const renderTariffCard = (meta: TariffMetadata, isRecommended: boolean) => {
    const Icon = meta.icon;

    return (
      <Card shadow="sm" padding="lg" withBorder style={{ height: '100%' }}>
        <Stack gap="md">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <Group gap="sm">
              <ThemeIcon size="xl" radius="md" color={meta.color}>
                <Icon size={24} />
              </ThemeIcon>
              <div>
                <Text size="xl" fw={700}>
                  {meta.name}
                </Text>
                <Text size="sm" c="dimmed">
                  {meta.type}
                </Text>
              </div>
            </Group>
            {isRecommended && (
              <Badge color="green" size="lg" variant="filled">
                Wybrana
              </Badge>
            )}
            {!isRecommended && (
              <Badge color="blue" size="lg" variant="light">
                Obecna
              </Badge>
            )}
          </Group>

          <Divider />

          {/* Description */}
          <div>
            <Group gap={6} mb={6}>
              <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
              <Text size="sm" fw={600}>
                Opis
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              {meta.description}
            </Text>
          </div>

          {/* Time Zones (if applicable) */}
          {meta.timeZones && (
            <div>
              <Group gap={6} mb={6}>
                <IconClock size={16} color="var(--mantine-color-indigo-6)" />
                <Text size="sm" fw={600}>
                  {meta.timeZones.description}
                </Text>
              </Group>
              <List size="sm" spacing={4}>
                {meta.timeZones.zones.map((zone, idx) => (
                  <List.Item key={idx}>
                    <Text size="sm" c="dimmed">
                      {zone}
                    </Text>
                  </List.Item>
                ))}
              </List>
            </div>
          )}

          {/* Best For */}
          <div>
            <Group gap={6} mb={6}>
              <IconUsers size={16} color="var(--mantine-color-violet-6)" />
              <Text size="sm" fw={600}>
                Dla kogo
              </Text>
            </Group>
            <List size="sm" spacing={4}>
              {meta.bestFor.map((item, idx) => (
                <List.Item key={idx}>
                  <Text size="sm" c="dimmed">
                    {item}
                  </Text>
                </List.Item>
              ))}
            </List>
          </div>

          {/* Advantages */}
          <div>
            <Group gap={6} mb={6}>
              <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />
              <Text size="sm" fw={600} c="green.7">
                Zalety
              </Text>
            </Group>
            <List
              size="sm"
              spacing={4}
              icon={
                <ThemeIcon color="green" size={18} radius="xl">
                  <IconCircleCheck size={12} />
                </ThemeIcon>
              }
            >
              {meta.advantages.map((advantage, idx) => (
                <List.Item key={idx}>
                  <Text size="sm">{advantage}</Text>
                </List.Item>
              ))}
            </List>
          </div>

          {/* Disadvantages */}
          <div>
            <Group gap={6} mb={6}>
              <IconAlertCircle size={16} color="var(--mantine-color-red-6)" />
              <Text size="sm" fw={600} c="red.7">
                Wady
              </Text>
            </Group>
            <List
              size="sm"
              spacing={4}
              icon={
                <ThemeIcon color="red" size={18} radius="xl">
                  <IconAlertCircle size={12} />
                </ThemeIcon>
              }
            >
              {meta.disadvantages.map((disadvantage, idx) => (
                <List.Item key={idx}>
                  <Text size="sm">{disadvantage}</Text>
                </List.Item>
              ))}
            </List>
          </div>
        </Stack>
      </Card>
    );
  };

  /**
   * Get behavioral change recommendations
   */
  const getBehaviorChanges = (): string[] => {
    const changes: string[] = [];

    // G11 to G12/G12as/G12w
    if (currentTariff === 'G11' && (recommendedTariff === 'G12' || recommendedTariff === 'G12as' || recommendedTariff === 'G12w')) {
      changes.push('Przenieś pranie, zmywanie i podgrzewanie wody na godziny nocne (22:00-6:00)');
      changes.push('Ładuj urządzenia elektroniczne i pojazdy elektryczne w nocy');
      changes.push('Korzystaj z taniego okresu popołudniowego (13:00-15:00) do energochłonnych zadań');
      if (recommendedTariff === 'G12w') {
        changes.push('Maksymalnie wykorzystaj weekendy do energochłonnych zadań (całodobowo tanie)');
      }
    }

    // G11 to G13/G13s
    if (currentTariff === 'G11' && (recommendedTariff === 'G13' || recommendedTariff === 'G13s')) {
      changes.push('Unikaj szczytowych godzin (7:00-13:00, 19:00-22:00 zimą)');
      changes.push('Maksymalnie wykorzystuj godziny "poza szczytem" (23:00-6:00)');
      changes.push('Planuj zużycie w trzech strefach czasowych - wymaga precyzyjnego zarządzania');
      changes.push('Pamiętaj o zmianach stref między sezonem letnim (IV-IX) a zimowym (X-III)');
    }

    // G12 to G12w
    if (currentTariff === 'G12' && recommendedTariff === 'G12w') {
      changes.push('Przenieś jak najwięcej zużycia na weekendy (całodobowo tanie)');
      changes.push('Weekendy to idealny moment na pranie, gotowanie, sprzątanie z użyciem energochłonnych urządzeń');
    }

    // G12 to G13
    if ((currentTariff === 'G12' || currentTariff === 'G12as' || currentTariff === 'G12w') && (recommendedTariff === 'G13' || recommendedTariff === 'G13s')) {
      changes.push('Przejście z dwóch stref czasowych na trzy - bardziej złożone planowanie');
      changes.push('Wykorzystaj bardzo tanią strefę "poza szczytem" (23:00-6:00, 16:00-19:00)');
      changes.push('Bezwzględnie unikaj najdroższej strefy szczytowej');
    }

    // Default if no specific changes
    if (changes.length === 0) {
      changes.push('Zapoznaj się ze strefami czasowymi nowej taryfy');
      changes.push('Stopniowo dostosuj swoje nawyki do tańszych godzin');
    }

    return changes;
  };

  const behaviorChanges = getBehaviorChanges();

  return (
    <Stack gap="xl">
      {/* Main Heading */}
      <Title order={2}>
        Szczegółowe porównanie taryf
      </Title>

      {/* Side-by-side comparison */}
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 6 }}>
          {renderTariffCard(currentMeta, false)}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          {renderTariffCard(recommendedMeta, true)}
        </Grid.Col>
      </Grid>

      {/* What Will Change Section */}
      <Paper p="lg" withBorder style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
        <Stack gap="md">
          <Group gap="sm">
            <ThemeIcon color="orange" size="lg" radius="xl">
              <IconArrowRight size={20} />
            </ThemeIcon>
            <Text size="lg" fw={700} c="orange.9">
              Co się zmieni po przejściu na {recommendedMeta.name}?
            </Text>
          </Group>

          {/* Savings Summary */}
          <Alert color="green" title="Oszczędności" icon={<IconCircleCheck size={20} />}>
            <Stack gap={6}>
              <Text size="md" fw={600}>
                Zaoszczędzisz {formatCurrency(savingsAmount)} rocznie
              </Text>
              <Text size="sm">
                To około {formatCurrency(savingsAmount / 12)} miesięcznie ({savingsPercent.toFixed(1)}% taniej)
              </Text>
            </Stack>
          </Alert>

          {/* Behavior Changes */}
          <div>
            <Group gap={6} mb="sm">
              <IconAlertCircle size={18} color="var(--mantine-color-orange-6)" />
              <Text size="md" fw={600}>
                Wymagane zmiany w zachowaniu
              </Text>
            </Group>
            <List
              size="sm"
              spacing={8}
              icon={
                <ThemeIcon color="orange" size={20} radius="xl">
                  <IconArrowRight size={14} />
                </ThemeIcon>
              }
            >
              {behaviorChanges.map((change, idx) => (
                <List.Item key={idx}>
                  <Text size="sm">{change}</Text>
                </List.Item>
              ))}
            </List>
          </div>

          {/* Potential Challenges */}
          <div>
            <Group gap={6} mb="sm">
              <IconAlertCircle size={18} color="var(--mantine-color-red-6)" />
              <Text size="md" fw={600} c="red.7">
                Potencjalne wyzwania
              </Text>
            </Group>
            <List size="sm" spacing={6}>
              <List.Item>
                <Text size="sm" c="dimmed">
                  Początkowy okres adaptacji - trzeba nauczyć się nowych nawyków (1-2 miesiące)
                </Text>
              </List.Item>
              <List.Item>
                <Text size="sm" c="dimmed">
                  Wymaga świadomego planowania użycia energochłonnych urządzeń
                </Text>
              </List.Item>
              <List.Item>
                <Text size="sm" c="dimmed">
                  Oszczędności będą niższe, jeśli nie przesuniesz zużycia na tańsze godziny
                </Text>
              </List.Item>
              {(recommendedTariff === 'G13' || recommendedTariff === 'G13s') && (
                <List.Item>
                  <Text size="sm" c="dimmed">
                    Taryfa {recommendedMeta.name} jest dostępna tylko u operatora Tauron
                  </Text>
                </List.Item>
              )}
            </List>
          </div>

          {/* Action Item */}
          <Alert color="blue" icon={<IconInfoCircle size={20} />}>
            <Text size="sm">
              <strong>Następny krok:</strong> Skontaktuj się ze swoim dostawcą energii i zapytaj o zmianę
              taryfy z {currentMeta.name} na {recommendedMeta.name}. Zmiana taryfy jest zwykle bezpłatna
              i zajmuje 2-4 tygodnie.
            </Text>
          </Alert>
        </Stack>
      </Paper>

      {/* Disclaimer */}
      <Alert color="gray" icon={<IconInfoCircle size={18} />}>
        <Text size="xs" c="dimmed">
          <strong>Uwaga:</strong> Przedstawione oszczędności są wartościami szacunkowymi obliczonymi na podstawie
          wprowadzonych danych o zużyciu energii. Rzeczywiste koszty i oszczędności mogą się różnić w zależności od:
          faktycznego zużycia energii, zmian cen energii i dystrybucji, indywidualnych warunków umowy z dostawcą,
          oraz stopnia dostosowania zachowań do stref czasowych taryfy. Zalecamy monitorowanie rzeczywistych rachunków
          przez pierwsze 3 miesiące po zmianie taryfy.
        </Text>
      </Alert>
    </Stack>
  );
}
